/**
 * Push Notification Manager
 * Handles service worker registration, subscription management, and notification display
 */

interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export class PushManager {
  private apiBaseUrl: string;
  private vapidPublicKey: string | null = null;

  constructor(apiBaseUrl: string) {
    this.apiBaseUrl = apiBaseUrl;
  }

  /**
   * Initialize push notifications
   */
  async initialize(userId: string): Promise<boolean> {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      // eslint-disable-next-line no-console
      console.warn("Push notifications are not supported in this browser");
      return false;
    }

    try {
      // Request notification permission first
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        // eslint-disable-next-line no-console
        console.log("🔔 Push notification permission not granted");
        return false;
      }

      // Register service worker
      const registration = await this.registerServiceWorker();
      if (!registration) return false;

      // Get VAPID public key
      await this.fetchVapidPublicKey();
      if (!this.vapidPublicKey) return false;

      // Subscribe to push notifications
      const subscription = await this.subscribeToPushNotifications(
        registration
      );
      if (!subscription) {
        // eslint-disable-next-line no-console
        console.log(
          "🔔 Push subscription not available (this is normal on localhost/HTTP)"
        );
        return false;
      }

      // Save subscription to backend
      await this.saveSubscriptionToBackend(userId, subscription);

      // eslint-disable-next-line no-console
      console.log("🔔 Push notifications initialized successfully");
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to initialize push notifications:", error);
      return false;
    }
  }

  /**
   * Register service worker
   */
  private async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });

      // Wait for service worker to be active
      await this.waitForServiceWorkerActive(registration);

      // eslint-disable-next-line no-console
      console.log("Service worker registered successfully");
      return registration;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Service worker registration failed:", error);
      return null;
    }
  }

  /**
   * Wait for service worker to become active
   */
  private async waitForServiceWorkerActive(
    registration: ServiceWorkerRegistration
  ): Promise<void> {
    // If there's an active service worker, we're good
    if (registration.active) {
      return;
    }

    // If there's an installing service worker, wait for it
    if (registration.installing) {
      await new Promise<void>((resolve) => {
        registration.installing!.addEventListener("statechange", () => {
          if (registration.installing!.state === "activated") {
            resolve();
          }
        });
      });
      return;
    }

    // Wait for any service worker to become active
    await new Promise<void>((resolve) => {
      const checkActive = () => {
        if (registration.active) {
          resolve();
        } else {
          setTimeout(checkActive, 100);
        }
      };
      checkActive();
    });
  }

  /**
   * Fetch VAPID public key from backend
   */
  private async fetchVapidPublicKey(): Promise<void> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/vapid-public-key`);
      const data = await response.json();
      this.vapidPublicKey = data.publicKey;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch VAPID public key:", error);
      throw error;
    }
  }

  /**
   * Subscribe to push notifications
   */
  private async subscribeToPushNotifications(
    registration: ServiceWorkerRegistration
  ): Promise<PushSubscription | null> {
    try {
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey!),
      });

      // eslint-disable-next-line no-console
      console.log("Push subscription created:", {
        hasSubscription: !!subscription,
        hasEndpoint: subscription?.endpoint ? "yes" : "no",
        endpoint: subscription?.endpoint || "null",
      });

      // Check if we got a valid subscription with endpoint
      if (!subscription || !subscription.endpoint) {
        // eslint-disable-next-line no-console
        console.warn(
          "Push subscription created but endpoint is null (common on localhost/HTTP)"
        );
        return null;
      }

      return subscription;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to subscribe to push notifications:", error);
      return null;
    }
  }

  /**
   * Save subscription to backend
   */
  private async saveSubscriptionToBackend(
    userId: string,
    subscription: PushSubscription
  ): Promise<void> {
    // Validate subscription has required properties
    if (!subscription || !subscription.endpoint) {
      throw new Error("Invalid subscription: missing endpoint");
    }

    const p256dhKey = subscription.getKey("p256dh");
    const authKey = subscription.getKey("auth");

    if (!p256dhKey || !authKey) {
      throw new Error("Invalid subscription: missing encryption keys");
    }

    const subscriptionData: PushSubscriptionData = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: this.arrayBufferToBase64(p256dhKey),
        auth: this.arrayBufferToBase64(authKey),
      },
    };

    // eslint-disable-next-line no-console
    console.log("Sending subscription data to backend:", {
      userId,
      endpoint: subscriptionData.endpoint,
      hasKeys: !!subscriptionData.keys,
      userAgent: navigator.userAgent.substring(0, 50) + "...",
      language: navigator.language,
    });

    try {
      const response = await fetch(`${this.apiBaseUrl}/api/push-subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          subscription: subscriptionData,
          userAgent: navigator.userAgent,
          language: navigator.language,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save subscription to backend:", error);
      throw error;
    }
  }

  /**
   * Convert VAPID key to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Convert ArrayBuffer to base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<boolean> {
    if (!("Notification" in window)) {
      return false;
    }

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission === "denied") {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === "granted";
  }

  /**
   * Show local notification (for testing)
   */
  showLocalNotification(title: string, options?: NotificationOptions): void {
    if (Notification.permission === "granted") {
      new Notification(title, {
        icon: "/icon-192x192.png",
        badge: "/icon-72x72.png",
        ...options,
      });
    }
  }
}

// Create global instance
const pushManager = new PushManager(
  window.location.hostname === "localhost"
    ? "http://localhost:3000"
    : "https://german-vibes-api.aek.dk"
);

interface NotificationStatus {
  success: boolean;
  message: string;
  isDevelopment: boolean;
}

interface WindowWithNotifications {
  initializeNotifications?: () => Promise<boolean>;
  getNotificationStatus?: () => NotificationStatus;
  notificationStatus?: NotificationStatus;
}

// Global function for UI integration
(window as Window & WindowWithNotifications).initializeNotifications =
  async (): Promise<boolean> => {
    try {
      // Use a mock user ID for testing
      const userId = "user-" + Math.random().toString(36).substr(2, 9);
      const success = await pushManager.initialize(userId);

      // Store status for UI
      (window as Window & WindowWithNotifications).notificationStatus = {
        success,
        message: success
          ? "Push notifications enabled successfully!"
          : window.location.hostname === "localhost"
          ? "Push notifications are not available on localhost (HTTP). They work on HTTPS production."
          : "Push notifications could not be enabled. Please check your browser settings.",
        isDevelopment: window.location.hostname === "localhost",
      };

      return success;
    } catch (error) {
      // Store error status for UI
      (window as Window & WindowWithNotifications).notificationStatus = {
        success: false,
        message:
          "An error occurred while setting up notifications: " +
          (error as Error).message,
        isDevelopment: window.location.hostname === "localhost",
      };
      return false;
    }
  };

// Add function to get notification status
(window as Window & WindowWithNotifications).getNotificationStatus = () => {
  const windowWithNotifications = window as Window & WindowWithNotifications;
  return (
    windowWithNotifications.notificationStatus || {
      success: false,
      message: "Notification system not initialized",
      isDevelopment: false,
    }
  );
};

export { pushManager };
